import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { LazyLoadImageModule } from 'ng-lazyload-image';
import { TranslateModule } from '@ngx-translate/core';
import { CdnUrlPipe } from '@shared/pipes';
import { ISearchResult } from '@core/models';
import { Router } from '@angular/router';
import { Search } from '@core/services';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

/**
 * Component responsible for rendering a marker card with image, title, rating and click actions
 */
@Component({
  selector: 'app-marker-card',
  imports: [LazyLoadImageModule, TranslateModule, CdnUrlPipe],
  templateUrl: './marker-card.html',
  styleUrl: './marker-card.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MarkerCard {
  /**
   * Router service for navigation
   */
  private readonly router = inject(Router);

  /**
   * Search service for managing marker selection
   */
  private readonly searchService = inject(Search);

  /**
   * Sanitizer service for handling HTML content
   */
  private readonly sanitizer = inject(DomSanitizer);

  /**
   * Input variable that receives the search result from the parent component
   */
  readonly marker = input.required<ISearchResult>();

  /**
   * Input variable that indicates if the card is displayed inside a carousel
   */
  readonly isCarousel = input<boolean>(false);

  /**
   * Input variable that determines whether to show the map button
   */
  readonly showBtnMap = input<boolean>(true);

  /**
   * Sanitized description of the marker to be displayed in the card footer
   */
  protected readonly description = computed<SafeHtml>(() => {
    const marker = this.marker();
    if (!marker?.language?.description_new) return '';

    return this.sanitizer.bypassSecurityTrustHtml(marker.language.description_new);
  });

  /**
   * Counter used to force reload when navigating to the map
   */
  private reload = 0;

  /**
   * Generates the complete geomarker location combining city and state
   */
  protected readonly location = computed<string>(() => {
    const city = this.marker()?.city;
    if (!city) return '';
    return `${city.language?.name} - ${city.state?.name}`;
  });

  /**
   * Gets the absolute value of the geomarker qualification
   */
  protected readonly qualification = computed<number>(() => {
    return Math.abs(this.marker()?.qualification || 0);
  });

  /**
   * Navigates the user to the search map showing the selected geomarker
   */
  protected goToMap(): void {
    this.reload++;
    this.searchService.markerSelectedSubject.next(this.marker());
    this.router.navigate([`search/`], {
      queryParams: {
        map: 1,
        marker: this.marker().id,
        reload: this.reload,
      },
      queryParamsHandling: 'merge',
    });
  }

  /**
   * Navigates the user to the profile of the selected geomarker
   */
  protected goToMarker() {
    this.router.navigate([`geomarker/${this.marker().slug}`]);
  }

  /**
   * Navigates the user to the selected city profile
   */
  protected goToCity() {
    const arrayUrl = this.marker().city.links.api.split('/');
    const idCity = arrayUrl[arrayUrl.length - 2];
    this.router.navigate([`cities/${idCity}`]);
  }
}
